// frontend/src/components/create/CreatePostOverlay.jsx - WITH MEDIA UPLOAD + COMMUNITY SEARCH

import React, { useEffect, useMemo, useState, useRef } from 'react';
import Modal from '../common/Modal';
import api from '../../services/api';
import Alert from '../common/Alert';
import Icon from '../common/Icon';
import PostForm from '../feed/PostForm.jsx';
import VoiceRecorder from '../VoiceRecorder/VoiceRecorder.jsx';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

import './CreatePostOverlay.css';

// API Base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://z-poel.onrender.com/api/v1';

// Poll Creator Component for MinimalComposer
const SimplePollCreator = ({ onPollCreated, onCancel }) => {
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollDuration, setPollDuration] = useState(24); // hours

  const handleAddOption = () => {
    if (pollOptions.length < 5) {
      setPollOptions([...pollOptions, '']);
    }
  };

  const handleRemoveOption = (index) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const handleCreatePoll = () => {
    const validOptions = pollOptions.filter(opt => opt.trim());
    if (pollQuestion.trim() && validOptions.length >= 2) {
      const closeDate = new Date();
      closeDate.setHours(closeDate.getHours() + pollDuration);
      
      onPollCreated({
        question: pollQuestion.trim(),
        options: validOptions,
        closeDate: closeDate.toISOString(),
        duration: pollDuration
      });
    }
  };

  return (
    <div className="poll-creator-section">
      <div className="poll-creator-header">
        <h4>Create a Poll</h4>
        <button type="button" onClick={onCancel} className="poll-cancel-btn">✕</button>
      </div>
      
      <input
        type="text"
        placeholder="Ask a question..."
        value={pollQuestion}
        onChange={(e) => setPollQuestion(e.target.value)}
        className="poll-question-input"
        maxLength={200}
      />
      
      <div className="poll-options-list">
        {pollOptions.map((option, index) => (
          <div key={index} className="poll-option-input-row">
            <input
              type="text"
              placeholder={`Option ${index + 1}`}
              value={option}
              onChange={(e) => handleOptionChange(index, e.target.value)}
              className="poll-option-input"
              maxLength={100}
            />
            {pollOptions.length > 2 && (
              <button
                type="button"
                onClick={() => handleRemoveOption(index)}
                className="remove-option-btn"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      
      {pollOptions.length < 5 && (
        <button
          type="button"
          onClick={handleAddOption}
          className="add-poll-option-btn"
        >
          + Add Option
        </button>
      )}
      
      <div className="poll-duration-selector">
        <label>Poll duration:</label>
        <select value={pollDuration} onChange={(e) => setPollDuration(Number(e.target.value))}>
          <option value={1}>1 hour</option>
          <option value={6}>6 hours</option>
          <option value={12}>12 hours</option>
          <option value={24}>1 day</option>
          <option value={72}>3 days</option>
          <option value={168}>1 week</option>
        </select>
      </div>
      
      <button
        type="button"
        onClick={handleCreatePoll}
        className="create-poll-confirm-btn"
        disabled={!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2}
      >
        Add Poll to Post
      </button>
    </div>
  );
};

// Media Preview Component
const MediaPreview = ({ mediaFiles, onRemove }) => {
  if (!mediaFiles || mediaFiles.length === 0) return null;

  return (
    <div className="media-preview-container">
      <div className="media-preview-grid">
        {mediaFiles.map((fileData, index) => (
          <div key={index} className="media-preview-item">
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="media-remove-btn"
              aria-label="Remove media"
            >
              ✕
            </button>
            
            {fileData.type.startsWith('image/') ? (
              <img
                src={fileData.preview}
                alt={`Upload preview ${index + 1}`}
                className="media-preview-image"
              />
            ) : fileData.type.startsWith('video/') ? (
              <div className="media-preview-video">
                <video src={fileData.preview} className="video-preview-element" />
                <div className="video-preview-overlay">
                  <Icon name="play" size={32} />
                </div>
              </div>
            ) : (
              <div className="media-preview-file">
                <Icon name="file" size={32} />
                <span className="file-name">{fileData.file.name}</span>
              </div>
            )}
            
            <div className="media-preview-info">
              <span className="media-file-size">
                {(fileData.file.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ⭐ Community Search Component - Search as you type
const CommunitySearch = ({ 
  selectedCommunity, 
  onSelectCommunity,
  disabled
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeout = useRef(null);
  const wrapperRef = useRef(null);

  const getCommunityIcon = (category) => {
    const icons = {
      'Sports': '⚽',
      'Tech': '💻',
      'Music': '🎵',
      'Food': '🍕',
      'Art': '🎨',
      'Gaming': '🎮',
      'Fitness': '💪',
      'Social': '👥',
      'Education': '📚',
      'Business': '💼',
      'Health': '🏥',
      'Travel': '✈️',
      'Fashion': '👗',
      'Movies': '🎬',
      'Books': '📖',
      'Photography': '📷',
      'Nature': '🌿',
      'Pets': '🐾',
      'Cars': '🚗',
      'Science': '🔬'
    };
    return icons[category] || '🏘️';
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search communities as user types
  const searchCommunities = async (query) => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/communities/search?q=${encodeURIComponent(query)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.communities || []);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error('[CommunitySearch] Error:', err);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSuggestions(true);

    // Debounce search
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      searchCommunities(value);
    }, 300);
  };

  const handleSelectCommunity = (community) => {
    onSelectCommunity(community);
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleClearCommunity = () => {
    onSelectCommunity(null);
    setSearchQuery('');
  };

  // If community is already selected, show the badge
  if (selectedCommunity) {
    return (
      <div style={styles.searchSelected}>
        <span style={styles.selectedLabel}>Posting to:</span>
        <div style={styles.selectedChip}>
          <span style={styles.chipIcon}>{getCommunityIcon(selectedCommunity.category)}</span>
          <span style={styles.chipName}>{selectedCommunity.name}</span>
          <button
            type="button"
            onClick={handleClearCommunity}
            style={styles.clearBtn}
            disabled={disabled}
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.searchWrapper} ref={wrapperRef}>
      <div style={styles.inputContainer}>
        <span style={styles.searchIcon}>🔍</span>
        <input
          type="text"
          placeholder="Search community to post in... (optional)"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
          style={styles.searchInput}
          disabled={disabled}
        />
        {isSearching && <span style={styles.spinner}>⏳</span>}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div style={styles.suggestionsDropdown}>
          {suggestions.map((community) => (
            <div
              key={community.id}
              style={styles.suggestionItem}
              onClick={() => handleSelectCommunity(community)}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
            >
              <span style={styles.suggestionIcon}>{getCommunityIcon(community.category)}</span>
              <div style={styles.suggestionInfo}>
                <span style={styles.suggestionName}>{community.name}</span>
                <span style={styles.suggestionMeta}>
                  {community.category} • 👥 {community.totalMembers || community.memberCount || 0} members
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results */}
      {showSuggestions && searchQuery.length >= 2 && !isSearching && suggestions.length === 0 && (
        <div style={styles.suggestionsDropdown}>
          <div style={styles.noResults}>
            No communities found for "{searchQuery}"
          </div>
        </div>
      )}
    </div>
  );
};

// Inline styles for CommunitySearch
const styles = {
  searchWrapper: {
    position: 'relative',
    marginBottom: '16px'
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    background: '#f8f9fa',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '10px 14px',
    gap: '10px'
  },
  searchIcon: {
    fontSize: '16px',
    opacity: 0.6
  },
  searchInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: '14px',
    outline: 'none'
  },
  spinner: {
    fontSize: '14px'
  },
  suggestionsDropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    zIndex: 1000,
    maxHeight: '250px',
    overflowY: 'auto'
  },
  suggestionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 14px',
    cursor: 'pointer',
    transition: 'background 0.15s'
  },
  suggestionIcon: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '10px',
    fontSize: '18px'
  },
  suggestionInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  suggestionName: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#333'
  },
  suggestionMeta: {
    fontSize: '12px',
    color: '#888'
  },
  noResults: {
    padding: '16px',
    textAlign: 'center',
    color: '#888',
    fontSize: '13px'
  },
  searchSelected: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
    padding: '12px 14px',
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
    borderRadius: '10px',
    border: '1px solid rgba(102, 126, 234, 0.3)'
  },
  selectedLabel: {
    fontSize: '13px',
    color: '#666'
  },
  selectedChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#fff',
    padding: '6px 12px',
    borderRadius: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  chipIcon: {
    fontSize: '16px'
  },
  chipName: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#333'
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    color: '#888',
    cursor: 'pointer',
    padding: '2px 6px',
    fontSize: '14px',
    lineHeight: 1,
    borderRadius: '50%'
  }
};

// MinimalComposer with Poll, Voice Note, Media Upload, and Community Search
function MinimalComposer({
  onSubmit,
  onCancel,
  isSubmitting,
  currentUser,
  categories,
  selectedCommunity,
  onSelectCommunity
}) {
  const [text, setText] = useState('');
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [pollData, setPollData] = useState(null);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [voiceNoteData, setVoiceNoteData] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  
  const fileInputRef = useRef(null);

  // Validation constants
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
  const MAX_FILES = 10;
  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
  const ACCEPTED_TYPES = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES];

  const canSubmit = useMemo(() => {
    const hasText = text.trim().length > 0;
    const hasPoll = Boolean(pollData);
    const hasVoiceNote = Boolean(voiceNoteData);
    const hasMedia = mediaFiles.length > 0;
    return Boolean(currentUser?.id) && (hasText || hasPoll || hasVoiceNote || hasMedia);
  }, [currentUser, text, pollData, voiceNoteData, mediaFiles]);

  const handleVoiceNoteRecorded = (recordingData) => {
    console.log('[MinimalComposer] Voice note received:', recordingData);
    
    if (recordingData && recordingData.blob) {
      setVoiceNoteData({
        audioBlob: recordingData.blob,
        duration: recordingData.duration || 0,
        transcription: recordingData.transcription || '',
        waveformData: recordingData.waveformData || []
      });
      setShowVoiceRecorder(false);
    } else {
      console.error('[MinimalComposer] Invalid voice recording data');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFiles = (files) => {
    setUploadError(null);

    // Check total file count
    if (mediaFiles.length + files.length > MAX_FILES) {
      setUploadError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      // Check file type
      if (!ACCEPTED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Unsupported file type`);
        return;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File too large (max 50MB)`);
        return;
      }

      // Create preview URL
      const preview = URL.createObjectURL(file);
      validFiles.push({
        file,
        preview,
        type: file.type,
        name: file.name,
        size: file.size
      });
    });

    if (errors.length > 0) {
      setUploadError(errors.join(', '));
    }

    if (validFiles.length > 0) {
      setMediaFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleRemoveMedia = (index) => {
    setMediaFiles(prev => {
      const newFiles = [...prev];
      // Revoke object URL to free memory
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files || []);
    handleFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const fd = new FormData();
    
    let contentToSend = text.trim();
    let contentType = 'text';
    
    // Handle voice note
    if (voiceNoteData) {
      contentType = 'voice_note';
      if (!contentToSend) {
        contentToSend = `🎤 Voice Note (${Math.round(voiceNoteData.duration)}s)`;
      }
      
      const voiceFile = new File(
        [voiceNoteData.audioBlob],
        'voice-note.ogg',
        { type: 'audio/ogg' }
      );
      
      fd.append('media', voiceFile);
      fd.append('duration', Math.round(voiceNoteData.duration));
      
      if (voiceNoteData.waveformData && voiceNoteData.waveformData.length > 0) {
        fd.append('waveform_data', JSON.stringify(voiceNoteData.waveformData));
      }
      
      if (voiceNoteData.transcription) {
        fd.append('voiceNoteTranscription', voiceNoteData.transcription);
      }
      
      console.log('[MinimalComposer] Adding voice note to post');
    }
    // Handle media files (images/videos)
    else if (mediaFiles.length > 0) {
      // Determine content type based on first file
      const firstFileType = mediaFiles[0].type;
      if (firstFileType.startsWith('image/')) {
        contentType = 'image';
      } else if (firstFileType.startsWith('video/')) {
        contentType = 'video';
      }

      // Append all media files
      mediaFiles.forEach((fileData) => {
        fd.append('media', fileData.file);
      });

      if (!contentToSend) {
        contentToSend = mediaFiles.length > 1 
          ? `📷 ${mediaFiles.length} media files`
          : '📷 Media post';
      }

      console.log('[MinimalComposer] Adding media files:', mediaFiles.length);
    }
    // Handle poll
    else if (pollData) {
      contentType = 'poll';
      if (!contentToSend) {
        contentToSend = `📊 Poll: ${pollData.question}`;
      }
      
      const pollOptionsArray = pollData.options.map(opt => 
        typeof opt === 'string' ? opt.trim() : opt.text
      );
      fd.append('poll_options', JSON.stringify(pollOptionsArray));
      
      console.log('[MinimalComposer] Sending poll with options:', pollOptionsArray);
    }
    
    fd.append('content', contentToSend || '<p></p>');
    fd.append('content_type', contentType);
    
    // Add community ID if posting to a community
    // Add community info if posting to a community
    if (selectedCommunity) {
      fd.append('community_id', selectedCommunity.id);
      fd.append('from_community_id', selectedCommunity.id);
      fd.append('from_community_name', selectedCommunity.name);
      fd.append('from_community_category', selectedCommunity.category || '');
      console.log('[MinimalComposer] Posting to community:', selectedCommunity.name);
    }
    
    await onSubmit(fd);
    
    // Cleanup and reset form after submission
    mediaFiles.forEach(fileData => {
      URL.revokeObjectURL(fileData.preview);
    });
    
    setText('');
    setPollData(null);
    setVoiceNoteData(null);
    setMediaFiles([]);
    setShowPollCreator(false);
    setShowVoiceRecorder(false);
    setUploadError(null);
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      mediaFiles.forEach(fileData => {
        URL.revokeObjectURL(fileData.preview);
      });
    };
  }, []);

  return (
    <form 
      className="minimal-post-composer" 
      onSubmit={handleSubmit}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {/* Community Search - Type to search */}
      <CommunitySearch
        selectedCommunity={selectedCommunity}
        onSelectCommunity={onSelectCommunity}
        disabled={isSubmitting}
      />

      <div className="field">
        <label htmlFor="min-content">What's on your mind?</label>
        <textarea
          id="min-content"
          rows={4}
          placeholder={selectedCommunity 
            ? `Share something with ${selectedCommunity.name}...` 
            : "Share something..."
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      {/* Upload Error Display */}
      {uploadError && (
        <div className="upload-error-message">
          <Icon name="alert-circle" size={16} />
          <span>{uploadError}</span>
          <button
            type="button"
            onClick={() => setUploadError(null)}
            className="error-dismiss-btn"
          >
            ✕
          </button>
        </div>
      )}

      {/* Media Preview */}
      {mediaFiles.length > 0 && (
        <MediaPreview
          mediaFiles={mediaFiles}
          onRemove={handleRemoveMedia}
        />
      )}

      {/* Media Buttons */}
      <div className="media-buttons">
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={isSubmitting || voiceNoteData || pollData}
        />
        
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="add-media-button"
          disabled={isSubmitting || voiceNoteData || pollData || mediaFiles.length >= MAX_FILES}
          title="Add photos or videos"
        >
          <span>📷</span>
          <span>Photo/Video</span>
        </button>

        {!showPollCreator && !pollData && (
          <button
            type="button"
            onClick={() => setShowPollCreator(true)}
            className="add-poll-button"
            disabled={isSubmitting || voiceNoteData || mediaFiles.length > 0}
          >
            <span>📊</span>
            <span>Poll</span>
          </button>
        )}
        
        {!showVoiceRecorder && !voiceNoteData && (
          <button
            type="button"
            onClick={() => setShowVoiceRecorder(true)}
            className="add-voice-button"
            disabled={isSubmitting || pollData || mediaFiles.length > 0}
          >
            <span>🎤</span>
            <span>Voice Note</span>
          </button>
        )}
      </div>

      {/* Media Upload Info */}
      {mediaFiles.length > 0 && (
        <div className="media-upload-info">
          <span className="media-count">
            {mediaFiles.length} file{mediaFiles.length !== 1 ? 's' : ''} selected
          </span>
          <span className="media-limit-info">
            (Max {MAX_FILES} files, 50MB each)
          </span>
        </div>
      )}

      {/* Poll Section */}
      {showPollCreator && (
        <SimplePollCreator
          onPollCreated={(poll) => {
            setPollData(poll);
            setShowPollCreator(false);
          }}
          onCancel={() => setShowPollCreator(false)}
        />
      )}

      {pollData && !showPollCreator && (
        <div className="poll-preview">
          <div className="poll-preview-header">
            <span>📊 Poll: "{pollData.question}"</span>
            <button
              type="button"
              onClick={() => {
                setPollData(null);
                setShowPollCreator(false);
              }}
              className="poll-remove-btn"
              disabled={isSubmitting}
            >
              Remove
            </button>
          </div>
          <small className="poll-preview-info">
            {pollData.options.length} options • Closes in {pollData.duration} hour{pollData.duration !== 1 ? 's' : ''}
          </small>
        </div>
      )}

      {/* Voice Recorder Section */}
      {showVoiceRecorder && (
        <VoiceRecorder
          onRecordingComplete={handleVoiceNoteRecorded}
          onCancel={() => setShowVoiceRecorder(false)}
          showTranscription={true}
          maxDuration={120}
        />
      )}

      {/* Voice Note Preview */}
      {voiceNoteData && !showVoiceRecorder && (
        <div className="voice-note-preview">
          <div className="voice-note-header">
            <span>🎤 Voice Note ({Math.round(voiceNoteData.duration)}s)</span>
            <button
              type="button"
              onClick={() => {
                setVoiceNoteData(null);
                setShowVoiceRecorder(false);
              }}
              className="voice-remove-btn"
              disabled={isSubmitting}
            >
              Remove
            </button>
          </div>
          {voiceNoteData.transcription && (
            <small className="voice-transcription-preview">
              "{voiceNoteData.transcription.substring(0, 100)}{voiceNoteData.transcription.length > 100 ? '...' : ''}"
            </small>
          )}
        </div>
      )}

      {/* Upload Progress */}
      {isSubmitting && uploadProgress > 0 && uploadProgress < 100 && (
        <div className="upload-progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <span className="progress-text">{uploadProgress}% uploaded</span>
        </div>
      )}

      <div className="actions">
        <button 
          type="button" 
          className="btn secondary" 
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="btn primary" 
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting ? 'Posting...' : selectedCommunity ? `Post to Feed & ${selectedCommunity.name}` : 'Post'}
        </button>
      </div>
    </form>
  );
}

const CreatePostOverlay = ({
  isOpen,
  onClose,
  onPostCreated,
  currentUser: currentUserProp,
  categories: categoriesProp,
  // Optional pre-selected community (for posting from CommunityDetailPage)
  preSelectedCommunity = null,
}) => {
  const { user: ctxUser } = useAuth();
  const currentUser = currentUserProp || ctxUser;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [categories, setCategories] = useState(categoriesProp || []);
  const [catsLoading, setCatsLoading] = useState(false);
  
  // Community state
  const [selectedCommunity, setSelectedCommunity] = useState(preSelectedCommunity);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSuccessMessage(null);
      // Reset community selection if no pre-selected community
      if (!preSelectedCommunity) {
        setSelectedCommunity(null);
      }
    }
  }, [isOpen, preSelectedCommunity]);

  // Set pre-selected community when prop changes
  useEffect(() => {
    if (preSelectedCommunity) {
      setSelectedCommunity(preSelectedCommunity);
    }
  }, [preSelectedCommunity]);

  const handlePostSubmit = async (formDataFromPostForm) => {
    setError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    if (!currentUser || !currentUser.id) {
      setError('You must be logged in to create a post.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Add user_id to the form data
      formDataFromPostForm.append('user_id', currentUser.id);

      // Log what we're sending for debugging
      console.log('[CreatePostOverlay] Submitting post with:');
      for (let [key, value] of formDataFromPostForm.entries()) {
        if (key === 'media') {
          console.log(`  ${key}: [File: ${value.name}, Type: ${value.type}, Size: ${value.size}]`);
        } else if (key === 'waveform_data') {
          console.log(`  ${key}: [Waveform data present]`);
        } else {
          console.log(`  ${key}:`, value);
        }
      }

      // Check if posting to a community
      const communityId = formDataFromPostForm.get('community_id');
      let response;
      const token = localStorage.getItem('token');
      
      // If community selected, add community info to the home feed post
      
      
      // STEP 1: Always post to home feed first
      console.log('[CreatePostOverlay] Step 1: Posting to home feed...');
      response = await api.posts.createPost(formDataFromPostForm);
      console.log('[CreatePostOverlay] Home feed post created:', response?.data);
      
      // STEP 2: If community selected, ALSO post to community
      if (communityId) {
        console.log('[CreatePostOverlay] Step 2: Also posting to community:', communityId);
        
        const postContent = formDataFromPostForm.get('content');
        const mediaFile = formDataFromPostForm.get('media');
        
        try {
          // If there's media, use the upload endpoint
          if (mediaFile && mediaFile instanceof File) {
            const communityFormData = new FormData();
            communityFormData.append('content', postContent);
            communityFormData.append('media', mediaFile);
            
            const fetchResponse = await fetch(`${API_BASE_URL}/communities/${communityId}/posts/upload`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              },
              body: communityFormData
            });
            
            if (fetchResponse.ok) {
              const data = await fetchResponse.json();
              console.log('[CreatePostOverlay] Community post created:', data.post);
            } else {
              const errorData = await fetchResponse.json();
              console.error('[CreatePostOverlay] Community post failed:', errorData);
            }
          } else {
            // Text-only post
            const fetchResponse = await fetch(`${API_BASE_URL}/communities/${communityId}/posts`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                content: postContent
              })
            });
            
            if (fetchResponse.ok) {
              const data = await fetchResponse.json();
              console.log('[CreatePostOverlay] Community post created:', data.post);
            } else {
              const errorData = await fetchResponse.json();
              console.error('[CreatePostOverlay] Community post failed:', errorData);
            }
          }
        } catch (communityErr) {
          // Don't fail the whole operation if community post fails
          console.error('[CreatePostOverlay] Community post error:', communityErr);
        }
      }
      
      const newPostData = response?.data?.post || response?.data;
      
      console.log('[CreatePostOverlay] Post created:', newPostData);

      const contentType = formDataFromPostForm.get('content_type');
      const messagePrefix = contentType === 'voice_note' ? 'Voice note' : 
                           contentType === 'poll' ? 'Poll' :
                           contentType === 'image' ? 'Image post' :
                           contentType === 'video' ? 'Video post' : 'Post';
      
      const successMsg = communityId 
        ? `${messagePrefix} shared to Home Feed & ${selectedCommunity?.name || 'community'}!`
        : `${messagePrefix} created successfully!`;
      
      setSuccessMessage(successMsg);

      if (typeof onPostCreated === 'function') {
        onPostCreated(newPostData);
      }

      // Close modal after short delay
      setTimeout(() => {
        setSuccessMessage(null);
        onClose?.();
      }, 1100);
      
    } catch (err) {
      console.error('[CreatePostOverlay] createPost error:', err?.response?.data || err);
      
      let msg = 'Failed to create post. Please try again.';
      
      // Handle specific backend errors
      if (err?.response?.data?.message?.includes('Invalid status code')) {
        msg = 'Server configuration error. Please contact support.';
      } else if (err?.response?.data?.error?.includes('Invalid file type')) {
        msg = 'File format not supported. Please use JPEG, PNG, GIF for images or MP4, WebM for videos.';
      } else if (err?.response?.data?.error?.includes('File too large')) {
        msg = 'File size too large. Maximum 50MB per file.';
      } else if (err?.response?.data?.error?.includes('Polls require at least two options')) {
        msg = 'Polls require at least two options. Please add more options.';
      } else if (err?.message?.includes('Not a member')) {
        msg = 'You must be a member of this community to post.';
      } else if (err?.response?.data?.message) {
        msg = err.response.data.message;
      } else if (err?.response?.data?.error) {
        msg = err.response.data.error;
      } else if (err?.message) {
        msg = err.message;
      }
      
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const showAuthBlocker = !currentUser;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Post">
      <div className="create-post-overlay-content">
        <header className="create-post-overlay-header">
          <h2 id="createPostTitle" className="create-post-overlay-title">
            {selectedCommunity ? `Post to ${selectedCommunity.name}` : 'Create New Post'}
          </h2>
          <button
            onClick={onClose}
            className="create-post-overlay-close-btn"
            aria-label="Close create post"
            disabled={isSubmitting}
          >
            <Icon name="x" size={24} />
          </button>
        </header>

        <div className="create-post-overlay-body">
          {showAuthBlocker && (
            <Alert
              type="warning"
              message="Please log in to create a post."
              dismissible={false}
            />
          )}

          {!showAuthBlocker && (
            <MinimalComposer
              onSubmit={handlePostSubmit}
              onCancel={onClose}
              isSubmitting={isSubmitting}
              currentUser={currentUser}
              categories={categories}
              selectedCommunity={selectedCommunity}
              onSelectCommunity={setSelectedCommunity}
            />
          )}
        </div>

        {successMessage && (
          <Alert
            type="success"
            message={successMessage}
            dismissible
            onClose={() => setSuccessMessage(null)}
          />
        )}
        {error && (
          <Alert
            type="error"
            message={error}
            dismissible
            onClose={() => setError(null)}
          />
        )}
      </div>
    </Modal>
  );
};

export default CreatePostOverlay;
